import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Scale, ArrowLeft, Check, Minus, ArrowRight } from "lucide-react";
import { LandingFooter } from "@/components/landing/CtaBanner";

// Values below mirror /app/backend/plans.py exactly. If plans.py changes, update this file.
const PLANS = [
  {
    id: "free",
    name: "Free",
    tagline: "Trying it out, or a light case load",
    monthly: 0,
    yearly: 0,
    trialNote: "No trial — free forever",
    ctaLabel: "Sign up",
    ctaTestid: "pricing-cta-free",
    features: [
      "Up to 10 active cases",
      "Up to 15 clients",
      "Up to 25 hearings per month",
      "500 MB document storage",
      "1 advocate (no team)",
      "Court diary & basic reports",
      "Tasks & in-app notifications",
    ],
    limitedNote:
      "Signing up as an Individual Advocate gives you the Pro Advocate 30-day trial first. When the trial ends without a paid subscription, the account continues on the Free plan with the limits above.",
  },
  {
    id: "pro",
    name: "Pro Advocate",
    tagline: "Independent advocates with a daily board",
    monthly: 499,
    yearly: 4990,
    trialNote: "30-day free trial · no card required",
    ctaLabel: "Start 30-day free trial",
    ctaTestid: "pricing-cta-pro",
    featured: true,
    features: [
      "Unlimited cases",
      "Unlimited clients",
      "Unlimited hearings",
      "5 GB document storage",
      "1 advocate (no team)",
      "Advanced court diary",
      "Full reports & CSV export",
      "Read-only client portal",
      "Client updates",
    ],
  },
  {
    id: "law_firm",
    name: "Law Firm",
    tagline: "Chambers working as a team",
    monthly: 999,
    yearly: 9990,
    trialNote: "30-day free trial · no card required",
    ctaLabel: "Start 30-day free trial",
    ctaTestid: "pricing-cta-law_firm",
    features: [
      "Everything in Pro Advocate",
      "Up to 5 team members",
      "10 GB document storage",
      "Team management with roles",
      "Case assignment across the team",
      "Firm-wide reports",
    ],
  },
];

// Feature comparison — cell strings are literal so it is easy to audit against plans.py.
const COMPARE_SECTIONS = [
  {
    title: "Limits",
    rows: [
      ["Active cases", "10", "Unlimited", "Unlimited"],
      ["Clients", "15", "Unlimited", "Unlimited"],
      ["Hearings per month", "25", "Unlimited", "Unlimited"],
      ["Document storage", "500 MB", "5 GB", "10 GB"],
      ["Team members", "1", "1", "5"],
    ],
  },
  {
    title: "Features",
    rows: [
      ["Court diary", true, "Advanced", "Advanced"],
      ["Tasks & reminders", true, true, true],
      ["In-app notifications", true, true, true],
      ["Basic reports", true, true, true],
      ["Full reports & CSV export", false, true, true],
      ["Read-only client portal", false, true, true],
      ["Client updates", "Basic", true, true],
      ["Team management & roles", false, false, true],
      ["Case assignment across team", false, false, true],
    ],
  },
];

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

const Cell = ({ v }) => {
  if (v === true) return <Check className="mx-auto h-4 w-4 text-gold-dark" strokeWidth={2.5} />;
  if (v === false) return <Minus className="mx-auto h-4 w-4 text-muted-foreground/60" />;
  return <span className="text-sm text-navy/85">{v}</span>;
};

export default function PricingPage() {
  const [yearly, setYearly] = useState(false);

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="pricing-brand" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
              <Scale className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="font-display text-xl tracking-tight text-navy">Advocate Diary</span>
          </Link>
          <Link
            to="/"
            data-testid="pricing-back-home"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy/70 transition-colors hover:bg-secondary hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="border-b border-border bg-navy px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold">Pricing</p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Plain pricing, in rupees
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
            One plan per practice. Start on a 30-day trial without adding a card. Upgrade or cancel from
            Settings at any time.
          </p>
        </div>
      </div>

      <main className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {/* Billing cycle toggle */}
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div
              data-testid="pricing-cycle-toggle"
              role="tablist"
              aria-label="Billing cycle"
              className="inline-flex rounded-md border border-border bg-white p-1"
            >
              <button
                type="button"
                onClick={() => setYearly(false)}
                aria-pressed={!yearly}
                data-testid="pricing-monthly-btn"
                className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
                  !yearly ? "bg-navy text-white" : "text-navy/70 hover:text-navy"
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setYearly(true)}
                aria-pressed={yearly}
                data-testid="pricing-yearly-btn"
                className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
                  yearly ? "bg-navy text-white" : "text-navy/70 hover:text-navy"
                }`}
              >
                Yearly <span className="text-gold-dark">· 2 months off</span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Prices in INR. Applicable taxes are added at checkout.
            </p>
          </div>

          {/* Plan cards */}
          <div className="mt-8 grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
            {PLANS.map((t) => (
              <div
                key={t.id}
                data-testid={`pricing-card-${t.id}`}
                className={`relative flex h-full flex-col rounded-xl bg-white p-7 transition-shadow ${
                  t.featured
                    ? "border-2 border-gold shadow-xl lg:-mt-3 lg:pb-9"
                    : "border border-border shadow-sm hover:shadow-md"
                }`}
              >
                {t.featured && (
                  <span className="absolute -top-3 left-7 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-navy">
                    Most popular
                  </span>
                )}
                <h3 className="font-display text-2xl text-navy">{t.name}</h3>
                <p className="mt-1 min-h-[2.5rem] text-xs text-muted-foreground">{t.tagline}</p>

                <p className="mt-4 flex items-baseline gap-1.5">
                  <span
                    data-testid={`pricing-amount-${t.id}`}
                    className="font-display text-4xl font-semibold text-navy"
                  >
                    {inr(yearly ? t.yearly : t.monthly)}
                  </span>
                  {t.monthly > 0 && (
                    <span className="text-sm text-muted-foreground">/{yearly ? "year" : "month"}</span>
                  )}
                </p>
                <p className="mt-1 text-[11px] font-medium uppercase tracking-wider text-gold-dark">
                  {t.trialNote}
                </p>

                <Link
                  to="/register"
                  data-testid={t.ctaTestid}
                  className={`mt-6 block rounded-md py-3 text-center text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                    t.featured
                      ? "bg-gold text-navy hover:bg-gold-light"
                      : "border border-navy/20 text-navy hover:bg-secondary"
                  }`}
                >
                  {t.ctaLabel}
                </Link>

                <ul className="mt-7 flex-1 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-sm text-navy/85">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>

                {t.limitedNote && (
                  <p className="mt-6 rounded-md bg-secondary/60 p-3 text-[11px] leading-relaxed text-muted-foreground">
                    {t.limitedNote}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Comparison table */}
          <section
            data-testid="pricing-compare"
            className="mt-16 overflow-hidden rounded-xl border border-border bg-white"
          >
            <div className="border-b border-border bg-secondary/40 px-6 py-4">
              <h2 className="font-display text-xl font-semibold tracking-tight text-navy">
                Compare the plans
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                What each plan actually includes today, so there are no surprises when you subscribe.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 text-xs uppercase tracking-wider text-gold-dark">
                    <th scope="col" className="px-6 py-3 font-semibold">
                      &nbsp;
                    </th>
                    <th scope="col" className="px-4 py-3 text-center font-semibold">
                      Free
                    </th>
                    <th scope="col" className="px-4 py-3 text-center font-semibold">
                      Pro Advocate
                    </th>
                    <th scope="col" className="px-4 py-3 text-center font-semibold">
                      Law Firm
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_SECTIONS.map((sec) => (
                    <React.Fragment key={sec.title}>
                      <tr className="border-b border-border bg-white">
                        <th
                          scope="rowgroup"
                          colSpan={4}
                          className="px-6 pb-2 pt-5 text-left font-display text-xs font-semibold uppercase tracking-[0.2em] text-navy/80"
                        >
                          {sec.title}
                        </th>
                      </tr>
                      {sec.rows.map((row) => (
                        <tr
                          key={sec.title + row[0]}
                          className="border-b border-border/70 last:border-b-0"
                        >
                          <th
                            scope="row"
                            className="px-6 py-3 text-left text-sm font-medium text-navy"
                          >
                            {row[0]}
                          </th>
                          <td className="px-4 py-3 text-center">
                            <Cell v={row[1]} />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Cell v={row[2]} />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Cell v={row[3]} />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* How the trial and billing work */}
          <section className="mt-14 grid gap-6 lg:grid-cols-2">
            <div
              data-testid="pricing-trial-panel"
              className="rounded-xl border border-border bg-white p-6 sm:p-8"
            >
              <h3 className="font-display text-lg font-semibold text-navy">How the free trial works</h3>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
                <li className="flex gap-3">
                  <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>
                    Every new account gets a <strong className="text-navy">30-day free trial</strong> of the
                    paid features. Sign up as an Individual Advocate for the Pro trial, or as a Law Firm for
                    the Law Firm trial.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>
                    No card, UPI or other payment instrument is collected to start the trial. You will only
                    see the payment step if you choose to subscribe.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>
                    Subscribe before or at the end of the trial to move onto a paid plan. If you don&rsquo;t
                    subscribe, an Individual Advocate account continues on the Free plan and paid-only
                    features become unavailable — your records stay on the account.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>
                    You can cancel a subscription at any time from Settings. Cancellation takes effect at the
                    end of the billing period you already paid for.
                  </span>
                </li>
              </ul>
            </div>

            <div
              data-testid="pricing-billing-panel"
              className="rounded-xl border border-border bg-white p-6 sm:p-8"
            >
              <h3 className="font-display text-lg font-semibold text-navy">Payments &amp; billing</h3>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
                <li className="flex gap-3">
                  <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>
                    Paid subscriptions are processed through{" "}
                    <strong className="text-navy">Razorpay</strong>. When you subscribe, checkout opens on
                    Razorpay and returns you to Advocate Diary on completion.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>
                    Advocate Diary does not store card numbers, CVV, UPI PIN, net banking credentials or any
                    other payment instrument credentials. Those are entered into and handled by Razorpay.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>
                    Prices shown are in INR and exclude applicable taxes, which the payment gateway adds at
                    checkout where they apply.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>
                    Refund eligibility and how to request a refund are covered in our{" "}
                    <Link
                      to="/refund-policy"
                      data-testid="pricing-inline-refund"
                      className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                    >
                      Cancellation &amp; Refund Policy
                    </Link>
                    .
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* Bottom CTA */}
          <section
            data-testid="pricing-bottom-cta"
            className="mt-14 flex flex-col items-start gap-4 rounded-xl border border-border bg-secondary/40 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8"
          >
            <div>
              <h3 className="font-display text-lg font-semibold text-navy">Not sure which plan fits?</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Start on the 30-day trial and pick a plan once you know how you actually use Advocate Diary.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="pricing-bottom-signup"
                className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-semibold text-navy shadow-sm transition-all hover:-translate-y-0.5 hover:bg-gold-light"
              >
                Create your account
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="pricing-bottom-contact"
                className="inline-flex items-center gap-2 rounded-md border border-navy/20 px-5 py-3 text-sm font-semibold text-navy transition-colors hover:bg-white"
              >
                Ask a question
              </Link>
            </div>
          </section>

          {/* Legal */}
          <p className="mt-10 text-xs leading-relaxed text-muted-foreground">
            By creating an account you agree to our{" "}
            <Link
              to="/terms"
              data-testid="pricing-legal-terms"
              className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy-policy"
              data-testid="pricing-legal-privacy"
              className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
            >
              Privacy Policy
            </Link>
            . Cancellation and refund handling is set out in the{" "}
            <Link
              to="/refund-policy"
              data-testid="pricing-legal-refund"
              className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
            >
              Cancellation &amp; Refund Policy
            </Link>
            . For any question about a plan or a payment, please{" "}
            <Link
              to="/contact"
              data-testid="pricing-legal-contact"
              className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
            >
              contact us
            </Link>
            .
          </p>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
